import { Slot } from '@radix-ui/react-slot'
import { motion } from 'motion/react'
import * as React from 'react'
import { tv, type VariantProps } from 'tailwind-variants'

import { Loader } from '@/components/ui/Loader'
import { cn } from '@/utils'

const buttonVariants = tv({
  base: 'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-normal transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 select-none cursor-pointer disabled:cursor-not-allowed [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
  variants: {
    theme: {
      default: 'bg-primary text-primary-foreground shadow hover:bg-primary/90',
      outline:
        'border border-input bg-white hover:bg-accent hover:text-accent-foreground',
      destructive:
        'border border-input bg-white text-red-600 hover:text-red-700 hover:bg-red-50 font-medium',
      secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/80',
      danger: 'bg-red-600 hover:bg-red-700 text-white',
      ghost: 'hover:bg-accent hover:text-accent-foreground',
      link: 'text-primary underline-offset-4 hover:underline',
    },
    size: {
      '2xs': 'h-3 rounded-md px-1 text-xs',
      xs: 'h-6 rounded-md px-2 text-xs',
      sm: 'h-8 rounded-md px-3 text-xs',
      md: 'h-9 px-4 py-2',
      lg: 'h-10 rounded-md px-8',
    },
    isIcon: {
      true: 'px-0',
    },
  },

  compoundVariants: [
    {
      isIcon: true,
      size: '2xs',
      class: 'h-3 w-3',
    },

    {
      isIcon: true,
      size: 'xs',
      class: 'h-6 w-6',
    },

    {
      isIcon: true,
      size: 'sm',
      class: 'h-8 w-8',
    },

    {
      isIcon: true,
      size: 'md',
      class: 'h-9 w-9',
    },

    {
      isIcon: true,
      size: 'lg',
      class: 'h-10 w-10',
    },
  ],

  defaultVariants: {
    theme: 'default',
    size: 'md',
  },
})

const LOADING_TEXT = {
  submit: 'Submitting...',
  button: 'Loading...',
  reset: 'Reseting...',
}

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
  asMotion?: boolean
  isLoading?: boolean
  loadingText?: string
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      theme,
      size,
      isIcon,
      asChild = false,
      asMotion = false,
      isLoading = false,
      disabled = false,
      loadingText,
      children,
      type = 'button',
      ...props
    },
    ref,
  ) => {
    const _loadingText = loadingText || LOADING_TEXT[type || 'button']
    const content = isLoading ? (
      <React.Fragment key="loading">
        <Loader /> {_loadingText}
      </React.Fragment>
    ) : (
      <React.Fragment key="children">{children}</React.Fragment>
    )

    if (asChild) {
      const child = React.Children.only(children) as React.ReactElement
      const slotChildren = isLoading
        ? React.cloneElement(child, undefined, content)
        : children

      return (
        <Slot
          className={cn(buttonVariants({ theme, size, isIcon, className }))}
          ref={ref}
          {...{ type }}
          {...props}
        >
          {slotChildren}
        </Slot>
      )
    }

    if (asMotion) {
      const MotionComp = motion.button
      return (
        <MotionComp
          className={cn(buttonVariants({ theme, size, isIcon, className }))}
          disabled={disabled || isLoading}
          ref={ref}
          type={type}
          {...(props as any)}
        >
          {content}
        </MotionComp>
      )
    }

    return (
      <button
        className={cn(buttonVariants({ theme, size, isIcon, className }))}
        disabled={disabled || isLoading}
        ref={ref}
        type={type}
        {...props}
      >
        {content}
      </button>
    )
  },
)
Button.displayName = 'Button'

export { Button, buttonVariants }
